import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { IPhoneRuleConfig } from "../../../setup/api/services/phoneRuleConfigs/models";
import { getCountryDisplayName } from "../../../setup/util/phoneRuleConfig.helpers";
import CountryFlag from "./CountryFlag";
import styles from "./styles.module.scss";

interface Props {
  countries: IPhoneRuleConfig[];
  selectedCountryCode?: string;
  onSelect: (country: IPhoneRuleConfig) => void;
  /** Hide the "+966" column — e.g. when picking a bank's country rather than a dial code. */
  hideDialCode?: boolean;
  className?: string;
}

const CheckIcon = () => (
  <svg
    className={styles.sheetCheck}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.333 4 6 11.333 2.667 8"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CountrySheet = ({
  countries,
  selectedCountryCode,
  onSelect,
  hideDialCode,
  className,
}: Props) => {
  const { i18n } = useTranslation();

  return (
    // The sheet may live inside an LTR-forced row, so set its own text direction from the
    // active language to render Arabic names right-to-left.
    <ul
      className={classNames(styles.sheet, className)}
      role="listbox"
      dir={i18n.dir()}>
      {countries.map((country) => {
        const isSelected = country.countryCode === selectedCountryCode;
        return (
          <li
            key={country.countryCode}
            role="option"
            aria-selected={isSelected}
            className={classNames(styles.sheetRow, {
              [styles.sheetRowSelected]: isSelected,
            })}
            onClick={() => onSelect(country)}>
            <span className={styles.sheetCountry}>
              <CountryFlag
                emoji={country.countryFlag}
                countryName={country.countryName}
                size={22}
              />
              <span className={styles.sheetName}>
                {getCountryDisplayName(country, i18n.language)}
              </span>
            </span>
            {!hideDialCode && (
              <span
                className={styles.sheetCode}>{`+${country.countryCode}`}</span>
            )}
            {isSelected && <CheckIcon />}
          </li>
        );
      })}
    </ul>
  );
};

export default CountrySheet;
